import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { ClientEditModal } from './ClientEditModal';
import { CohortTransitionModal } from './CohortTransitionModal';
import { useAuth } from '../../shared/auth';
import {
  startOfWeekMonIso,
  todayLocalIso,
  fmtDate,
  fmtTime,
  addDaysIso,
} from '../../shared/dates';
import { CohortDescriptions, ServiceLabels, type ServiceType } from '@census/shared';
import { Icon } from '../../ui/Icon';
import { cohortNameOrCode, useCohorts } from '../../shared/cohorts';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { byCode: cohortByCode } = useCohorts();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [transitionOpen, setTransitionOpen] = useState(false);
  const weekStart = startOfWeekMonIso(todayLocalIso());

  // Attendance window. Defaults to the API's built-in 28-day trailing window
  // (left blank → server defaults). Operator can override with the date inputs
  // to "check" any historical window.
  const today = todayLocalIso();
  const defaultFrom = addDaysIso(today, -27); // 28-day window inclusive
  const [windowFrom, setWindowFrom] = useState<string>(defaultFrom);
  const [windowTo, setWindowTo] = useState<string>(today);
  // Applied values drive the query; the inputs above are the editable draft.
  // This way the user picks a range, clicks Run, then sees the report — same
  // UX as the Reports page.
  const [appliedFrom, setAppliedFrom] = useState<string>(defaultFrom);
  const [appliedTo, setAppliedTo] = useState<string>(today);

  const { data: client } = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get(`/clients/${id}`).then((r) => r.data),
    enabled: !!id,
  });
  const { data: weekData } = useQuery({
    queryKey: ['client-week', id, weekStart],
    queryFn: () =>
      api.get(`/schedule/client/${id}`, { params: { weekStart } }).then((r) => r.data),
    enabled: !!id,
  });
  const { data: attendance } = useQuery({
    queryKey: ['client-attendance', id, appliedFrom, appliedTo],
    queryFn: () =>
      api
        .get(`/reports/client/${id}/attendance`, {
          params: { from: appliedFrom, to: appliedTo },
        })
        .then((r) => r.data),
    enabled: !!id,
  });
  const { data: clinicInfo } = useQuery({
    queryKey: ['clinic-info'],
    queryFn: () => api.get('/settings/clinic_info').then((r) => r.data),
  });
  const { data: cohortHistory } = useQuery({
    queryKey: ['client-cohort-history', id],
    queryFn: () => api.get(`/clients/${id}/cohort-history`).then((r) => r.data),
    enabled: !!id,
  });
  // Housing placements for this client. Includes both current (endDate=null)
  // and historical placements so the operator can see the move history.
  const { data: housingPlacements } = useQuery({
    queryKey: ['client-housing-placements', id],
    queryFn: () => api.get(`/housing/placements?clientId=${id}`).then((r) => r.data),
    enabled: !!id,
  });

  if (!client) return <div className="text-sm text-ink-subtle">Loading...</div>;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
            <span className="font-mono text-brand-700">{client.cohortId}</span> · {client.firstName}{' '}
            {client.lastName}
          </h1>
          <p className="text-sm text-ink-subtle">
            Cohort {client.cohort}: {cohortNameOrCode(client.cohort, cohortByCode)} ·{' '}
            <span className={client.status === 'ACTIVE' ? 'pill-success' : 'pill-muted'}>
              {client.status}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {client.externalRef && clinicInfo?.emrClientUrlTemplate && (
            <a
              className="btn-secondary h-9"
              href={clinicInfo.emrClientUrlTemplate.replace('{externalRef}', encodeURIComponent(client.externalRef))}
              target="_blank"
              rel="noreferrer"
            >
              Open in EMR ↗
            </a>
          )}
          {user?.role === 'ADMIN' && (
            <>
              <button className="btn-secondary h-9" onClick={() => setEditOpen(true)}>
                Edit client
              </button>
              {client.status === 'ACTIVE' && (
                <button
                  className="btn-secondary h-9"
                  onClick={() => setTransitionOpen(true)}
                  title="Step-up / step-down — close current cohort, open a new one, and re-run schedule"
                >
                  Change cohort
                </button>
              )}
            </>
          )}
          <Link className="btn-secondary h-9" to={`/print/client/${id}`} target="_blank">
            <Icon name="print" size={14} /> Print week
          </Link>
          <Link className="btn-ghost h-9" to="/clients">
            ← Back
          </Link>
        </div>
      </header>
      <ClientEditModal open={editOpen} client={client} onClose={() => setEditOpen(false)} />
      {transitionOpen && (
        <CohortTransitionModal
          client={client}
          onClose={() => setTransitionOpen(false)}
          onTransitioned={() => {
            setTransitionOpen(false);
            qc.invalidateQueries({ queryKey: ['client', id] });
            qc.invalidateQueries({ queryKey: ['client-cohort-history', id] });
            qc.invalidateQueries({ queryKey: ['client-week', id] });
            qc.invalidateQueries({ queryKey: ['client-attendance', id] });
            toast.success('Cohort transitioned — schedule rebuilt');
          }}
        />
      )}

      {/* Housing card — first thing after the header so case managers see
          location + room + phone + move-in immediately on opening the
          profile. Hidden when client has no placements (NEVER_HOUSED). */}
      {housingPlacements && housingPlacements.length > 0 && (
        <HousingCard placements={housingPlacements} />
      )}

      {/* Cohort history timeline. One pill per assignment, currently-active
          row marked. Hidden when there's only the single initial row to keep
          the page tight for never-transitioned clients. */}
      {cohortHistory && cohortHistory.length > 1 && (
        <section className="card p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
            <Icon name="layers" size={14} /> Cohort history
            <span className="text-2xs font-normal text-ink-subtle">
              ({cohortHistory.length} cohort{cohortHistory.length === 1 ? '' : 's'})
            </span>
          </h2>
          <ol className="space-y-2">
            {cohortHistory.map((a: any) => (
              <li
                key={a.id}
                className={clsx(
                  'flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm',
                  a.isCurrent
                    ? 'border-brand-300 bg-brand-50/40'
                    : 'border-line-subtle bg-surface-subtle/40',
                )}
              >
                <span className="font-mono font-semibold text-brand-700">{a.cohortId}</span>
                <span className="font-medium text-ink">
                  {a.cohort} — {a.cohortLabel}
                </span>
                <span className="text-xs text-ink-subtle">
                  {a.startDate} → {a.endDate ?? 'current'}
                </span>
                {a.isCurrent && <span className="pill-success">Active</span>}
                {a.reason && (
                  <span className="ml-2 text-2xs text-ink-subtle italic">{a.reason}</span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Attendance flags strip */}
      {attendance && attendance.flags.length > 0 && (
        <section className="card border-l-4 border-l-amber-500 p-4">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
            <Icon name="check-square" size={14} /> Attendance follow-up
            <span className="pill-warn">{attendance.flags.length}</span>
          </h2>
          <ul className="space-y-1.5">
            {attendance.flags.map((f: any, i: number) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span
                  className={
                    f.severity === 'HIGH'
                      ? 'pill-danger'
                      : f.severity === 'MEDIUM'
                        ? 'pill-warn'
                        : 'pill-info'
                  }
                >
                  {f.severity}
                </span>
                <span className="text-ink">{f.message}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="grid gap-4 lg:grid-cols-3">
        <Card title="Profile" className="lg:col-span-1">
          <Field label="Cohort ID" value={client.cohortId} />
          <Field label="EMR client ID" value={client.externalRef ?? '—'} />
          <Field label="Case manager" value={client.caseManagerName ?? '— Unassigned —'} />
          <Field label="Required services" value={(client.requiredServices ?? []).join(', ')} />
          <Field label="Telehealth" value={client.telehealthAllowed ? 'Yes' : 'No'} />
          <Field label="Home / community" value={client.homeCommunityOk ? 'Yes' : 'No'} />
          <Field label="IOP track" value={client.iopTrackName ?? '—'} />
          <Field label="Nursing day" value={client.nursingGroupDay ?? '—'} />
          <Field label="Preferred days" value={(client.preferredDays ?? []).join(', ') || '—'} />
          <Field label="Intake date" value={client.intakeDate} />
          {client.dischargedDate && <Field label="Discharged" value={client.dischargedDate} />}
        </Card>

        {/* Per-service attendance rollup */}
        <Card title="Attendance by service" className="lg:col-span-2">
          <AttendanceWindowControls
            from={windowFrom}
            to={windowTo}
            onFromChange={setWindowFrom}
            onToChange={setWindowTo}
            onRun={() => {
              setAppliedFrom(windowFrom);
              setAppliedTo(windowTo);
            }}
            onPreset={(fromIso, toIso) => {
              setWindowFrom(fromIso);
              setWindowTo(toIso);
              setAppliedFrom(fromIso);
              setAppliedTo(toIso);
            }}
            today={today}
          />
          {attendance ? (
            <PerServiceTable byService={attendance.byService} overall={attendance.overall} window={attendance.window} />
          ) : (
            <p className="text-sm text-ink-subtle">Loading…</p>
          )}
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card title={`Week of ${fmtDate(weekStart)}`}>
          {weekData?.instances?.length ? (
            <ul className="divide-y divide-line-subtle text-sm">
              {weekData.instances.map((i: any) => {
                // Surface the most useful navigation detail: for groups, the
                // room (where to physically go); for individual visits, the
                // provider name. Falls back to the resource name (which IS
                // the provider for individuals; for groups it's the group name).
                const sub =
                  i.resourceType === 'GROUP' && i.roomName
                    ? `${i.resourceName} · 📍 ${i.roomName}`
                    : i.resourceName;
                return (
                  <li
                    key={i.id}
                    className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 py-1.5"
                  >
                    <span className="font-mono text-2xs text-ink-subtle">
                      {fmtDate(i.scheduledDate).slice(0, 3)} {fmtTime(i.startMin)}
                    </span>
                    <span className="font-medium text-ink">
                      {ServiceLabels[i.serviceType as ServiceType] ?? i.serviceType}
                    </span>
                    <span className="truncate text-2xs text-ink-subtle" title={sub}>
                      {sub}
                      {i.viaTelehealth ? ' · 📹' : ''}
                    </span>
                    <StatusPill status={i.status} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-ink-subtle">No instances generated yet this week.</p>
          )}
          <p className="mt-2 text-2xs text-ink-subtle">
            {weekStart} → {addDaysIso(weekStart, 6)}
          </p>
        </Card>

        <Card title="Recent attendance">
          {attendance?.recentInstances?.length ? (
            <ul className="divide-y divide-line-subtle text-sm">
              {attendance.recentInstances
                .slice()
                .reverse()
                .slice(0, 12)
                .map((i: any) => (
                  <li
                    key={i.id}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-3 py-1.5"
                  >
                    <span className="font-mono text-2xs text-ink-subtle">{i.date}</span>
                    <span className="text-ink">
                      {ServiceLabels[i.serviceType as ServiceType] ?? i.serviceType}
                      {i.lateMinutes ? (
                        <span className="ml-1 text-2xs text-amber-700">+{i.lateMinutes}m</span>
                      ) : null}
                    </span>
                    <StatusPill status={i.status} />
                  </li>
                ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-subtle">No history in the window.</p>
          )}
        </Card>
      </section>

    </div>
  );
}

/**
 * Inline housing card rendered on the Client detail page. Shows the current
 * placement (if any) prominently with house metadata + click-to-call house
 * manager phone, and the move history collapsed below.
 *
 * "Pending move-out" flag (orange banner) appears when expectedMoveOutDate
 * is within 7 days — clinically relevant signal for the case manager.
 */
function HousingCard({ placements }: { placements: any[] }) {
  const current = placements.find((p) => p.isCurrent);
  const history = placements.filter((p) => !p.isCurrent);
  const today = todayLocalIso();
  const pendingMoveOut =
    current?.expectedMoveOutDate &&
    new Date(current.expectedMoveOutDate).getTime() - new Date(today).getTime() <=
      7 * 86_400_000;

  return (
    <section className="card p-4">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
        <Icon name="building" size={14} /> Housing
        {current && (
          <span className="pill-info">{current.locationCode}</span>
        )}
      </h2>

      {!current && (
        <p className="text-sm text-ink-subtle">
          Not currently in housing (last placement ended {history[0]?.endDate ?? '—'}).
        </p>
      )}

      {current && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="text-base font-semibold text-ink">
              {current.locationDisplayName}
            </div>
            <div className="text-xs text-ink-subtle">{current.locationCode}</div>
            <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <span className="text-ink-subtle">Room</span>
              <span className="font-medium">{current.roomNumber || '—'}</span>
              <span className="text-ink-subtle">Move-in</span>
              <span>{current.startDate}</span>
              <span className="text-ink-subtle">Phone</span>
              <span className="font-mono text-xs">
                {current.contactPhone ? (
                  <a className="hover:underline" href={`tel:${current.contactPhone}`}>
                    {current.contactPhone}
                  </a>
                ) : (
                  '—'
                )}
              </span>
              {current.lockerNumber && (
                <>
                  <span className="text-ink-subtle">Locker</span>
                  <span>{current.lockerNumber}</span>
                </>
              )}
              {current.restrictionText && (
                <>
                  <span className="text-ink-subtle">Restriction</span>
                  <span className="text-xs">{current.restrictionText}</span>
                </>
              )}
              {current.workStatus && (
                <>
                  <span className="text-ink-subtle">Work</span>
                  <span className="text-xs">{current.workStatus}</span>
                </>
              )}
            </div>
            {current.notes && (
              <p className="mt-2 rounded bg-surface-subtle/60 p-2 text-xs text-ink-muted">
                <span className="font-semibold text-ink-subtle">Note: </span>
                {current.notes}
              </p>
            )}
          </div>

          {pendingMoveOut && (
            <div className="rounded-md border-l-4 border-l-amber-500 bg-amber-50/60 p-3 text-sm">
              <div className="font-semibold text-amber-900">Pending move-out</div>
              <div className="mt-1 text-xs text-amber-800">
                Expected to leave housing on <strong>{current.expectedMoveOutDate}</strong> —
                follow up with case manager about continuity of care.
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-4 border-t border-line-subtle pt-3">
          <summary className="cursor-pointer text-xs text-ink-subtle">
            Move history ({history.length} prior {history.length === 1 ? 'placement' : 'placements'})
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {history.map((p) => (
              <li key={p.id} className="flex items-baseline justify-between gap-2">
                <span>
                  <span className="font-mono text-ink-subtle">{p.locationCode}</span>{' '}
                  {p.locationDisplayName} · Room {p.roomNumber || '—'}
                </span>
                <span className="text-ink-subtle">
                  {p.startDate} → {p.endDate}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function PerServiceTable({
  byService,
  overall,
  window,
}: {
  byService: any[];
  overall: any;
  window: { from: string; to: string };
}) {
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-2 text-2xs text-ink-subtle">
        <span>
          Window {window.from} → {window.to}
        </span>
        <span>
          Overall:{' '}
          {overall.attended + overall.noShow > 0 ? (
            <>
              <span className="font-semibold text-ink">
                {(overall.rate * 100).toFixed(0)}%
              </span>{' '}
              ({overall.attended}/{overall.attended + overall.noShow})
            </>
          ) : (
            <span className="font-semibold text-ink-subtle">—</span>
          )}
        </span>
      </div>
      {byService.length === 0 ? (
        <p className="text-sm text-ink-subtle">No services tracked in this window.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-2xs uppercase tracking-wide text-ink-subtle">
            <tr className="border-b border-line-subtle">
              <th className="py-1.5 text-left font-medium">Service</th>
              <th className="px-2 py-1.5 text-right font-medium">Sched</th>
              <th className="px-2 py-1.5 text-right font-medium">Att</th>
              <th className="px-2 py-1.5 text-right font-medium">Late</th>
              <th className="px-2 py-1.5 text-right font-medium">No-show</th>
              <th className="px-2 py-1.5 text-right font-medium">Rate</th>
              <th className="px-2 py-1.5 text-left font-medium">Last</th>
            </tr>
          </thead>
          <tbody>
            {byService.map((s) => (
              <tr key={s.serviceType} className="border-b border-line-subtle">
                <td className="py-1.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        'inline-block h-2 w-2 rounded-full',
                        s.kind === 'GROUP' ? 'bg-brand-500' : 'bg-emerald-500',
                      )}
                    />
                    <span className="text-ink">
                      {ServiceLabels[s.serviceType as ServiceType] ?? s.serviceType}
                    </span>
                  </div>
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{s.scheduled}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">
                  {s.attended}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-amber-700">{s.late}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">{s.noShow}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {(() => {
                    // Rate is meaningful only when there's at least one
                    // TERMINAL event in the window. With scheduled-only
                    // (fresh intake, no completed sessions yet) we show
                    // "—" rather than a misleading 0%.
                    const hasTerminal =
                      s.attended + s.noShow + s.late + s.cancelled + s.excused > 0;
                    if (!hasTerminal) {
                      return <span className="pill-muted">—</span>;
                    }
                    const cls =
                      s.rate >= 0.9
                        ? 'pill-success'
                        : s.rate >= 0.75
                          ? 'pill-warn'
                          : 'pill-danger';
                    return (
                      <span className={cls}>{`${(s.rate * 100).toFixed(0)}%`}</span>
                    );
                  })()}
                </td>
                <td className="px-2 py-1.5">
                  {s.lastDate ? (
                    <span className="flex items-center gap-1.5 text-2xs">
                      <span className="text-ink-subtle">{s.lastDate}</span>
                      <StatusPill status={s.lastStatus ?? 'SCHEDULED'} />
                    </span>
                  ) : (
                    <span className="text-2xs text-ink-subtle">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Card({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('card p-4', className)}>
      <h2 className="mb-3 text-sm font-semibold text-ink">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm">
      <span className="text-ink-subtle">{label}</span>
      <span className="font-medium text-ink">{value}</span>
    </div>
  );
}

/**
 * Date-range picker for the client attendance view. Defaults to the trailing
 * 28-day window the API uses, but the operator can pick any range and click
 * "Run" to refresh. Quick presets cover the common asks (this month, last 30,
 * 90, year-to-date, all time).
 */
function AttendanceWindowControls({
  from,
  to,
  onFromChange,
  onToChange,
  onRun,
  onPreset,
  today,
}: {
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onRun: () => void;
  onPreset: (from: string, to: string) => void;
  today: string;
}) {
  const presets: Array<{ label: string; from: string; to: string }> = [
    { label: 'Last 28 days', from: addDaysIso(today, -27), to: today },
    { label: 'Last 90 days', from: addDaysIso(today, -89), to: today },
    { label: 'Year to date', from: `${today.slice(0, 4)}-01-01`, to: today },
    // All-time: the server enforces a sensible lower bound; pick a very early
    // ISO date so any intake date will be inside the window.
    { label: 'All time', from: '2000-01-01', to: today },
  ];

  return (
    <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-line-subtle bg-surface-subtle/60 p-2.5">
      <label className="flex flex-col text-2xs text-ink-subtle">
        From
        <input
          type="date"
          className="input h-8 w-40"
          value={from}
          max={to}
          onChange={(e) => onFromChange(e.target.value)}
        />
      </label>
      <label className="flex flex-col text-2xs text-ink-subtle">
        To
        <input
          type="date"
          className="input h-8 w-40"
          value={to}
          min={from}
          max={today}
          onChange={(e) => onToChange(e.target.value)}
        />
      </label>
      <button type="button" className="btn-primary h-8 px-3" onClick={onRun}>
        Run
      </button>
      <div className="ml-auto flex flex-wrap gap-1">
        {presets.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => onPreset(p.from, p.to)}
            className="rounded border border-line bg-white px-2 py-1 text-2xs text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'ATTENDED'
      ? 'pill-success'
      : status === 'LATE'
        ? 'pill-warn'
        : status === 'NO_SHOW'
          ? 'pill-danger'
          : status === 'SCHEDULED'
            ? 'pill-info'
            : 'pill-muted';
  return <span className={cls}>{status}</span>;
}
